import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';

import { selectShowInboxTrigger } from '@ninety/platform/state/app-global/notifications/notification.selectors';
import { TerraCounterModule, TerraIconModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';

import { INBOX_ENTRY_POINT_STATE_KEY, InboxEntryPoint } from '../../_state/inbox-entry-point';
import { InboxSelectors } from '../../_state/inbox.selectors';

/**
 * Navigates to `/inbox` rather than opening an overlay: inbox content is long-form and an alert
 * about a specific KPI needs to be deep-linkable.
 *
 * A bell, not an envelope: this is the one notification affordance in the toolbar once the flag is
 * on — `selectShowNotificationFeedTrigger` hides the feed's own bell — so it keeps the icon readers
 * already look for rather than moving the slot to a new symbol.
 *
 * `terra-counter`, not `NinetyNotificationBadgeDirective` — `terra-badge` is icon-only by
 * construction and can't render a number. `terra-counter` has no positioning of its own, hence the
 * `position: relative` wrapper.
 */
@Component({
  selector: 'ninety-inbox-trigger',
  imports: [RouterLink, ButtonComponent, TerraIconModule, TerraCounterModule],
  template: `
    @if (showInbox()) {
      <span class="inbox-trigger">
        <ninety-button
          stroked
          icon
          ariaLabel="Open inbox"
          data-cy="inbox-trigger"
          [routerLink]="['/inbox']"
          [state]="entryPointState">
          <terra-icon icon="bell" size="20" />
        </ninety-button>
        @if (badgeCount() > 0) {
          <terra-counter class="inbox-trigger__count" color="red">{{ badgeCount() }}</terra-counter>
        }
      </span>
    }
  `,
  styles: [
    `
      .inbox-trigger {
        position: relative;
        display: inline-block;
      }

      .inbox-trigger__count {
        position: absolute;
        top: -4px;
        right: -6px;
        pointer-events: none;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InboxTriggerComponent {
  private readonly store = inject(Store);

  /** Names this button on the navigation, so the page it opens can report where it was opened from. */
  protected readonly entryPointState = { [INBOX_ENTRY_POINT_STATE_KEY]: 'topBarBadge' satisfies InboxEntryPoint };

  protected readonly showInbox = this.store.selectSignal(selectShowInboxTrigger);
  protected readonly badgeCount = this.store.selectSignal(InboxSelectors.selectBadgeCount);
}
